import type { ChatbotMode } from '@/components-restructure/ChatbotPage/useChatbotMode';
import { useIsSharedChat } from '@/components/Chat/useIsSharedChat';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/trpc/react';
import type { RouterOutputs } from '@unriddle/api';
import type { Chat } from '@unriddle/db/types';
import type { LinkedSource } from '@unriddle/validators/chat.entity';
import { useEffect, useMemo } from 'react';

export type WorkspaceItem =
  RouterOutputs['workspace']['all']['entities'][number];

export const useWorkspace = () => {
  const { isLoggedIn } = useAuth();
  const { data, isLoading } = api.workspace.all.useQuery(undefined, {
    refetchOnWindowFocus: false,
    enabled: isLoggedIn,
  });

  return { data, isLoading };
};

export const useWorkspaceWithFolderNames = () => {
  const { data } = useWorkspace();

  const itemsWithFolders = useMemo(() => {
    return data?.entities.map((item) => {
      const folder = data?.entities.find((d) => d.id === item.groupId);

      return {
        ...item,
        folder: folder?.name ?? null,
      };
    });
  }, [data]);

  return itemsWithFolders;
};

export const useWorkspaceActions = () => {
  const utils = api.useUtils();

  const addChatbot = (
    item: Omit<WorkspaceItem, 'chats'> & { chats?: WorkspaceItem[] },
  ) => {
    utils.workspace.all.setData(undefined, (old) => {
      if (!old) return old;

      return {
        ...old,
        entities: [...old.entities, { ...item, chats: item.chats ?? [] }],
      };
    });
  };

  const deleteChatbot = (id: string) => {
    utils.workspace.all.setData(undefined, (old) => {
      if (!old) return old;

      return {
        ...old,
        entities: old.entities.filter((item) => item.id !== id),
      };
    });
  };

  const updateChatbot = (
    id: string,
    item: Partial<WorkspaceItem>,
    type: 'CHAT' | 'CHATBOT' = 'CHATBOT',
  ) => {
    utils.workspace.all.setData(undefined, (old) => {
      if (!old) return old;

      return {
        ...old,
        entities: old.entities.map((entity) => {
          if (entity.id === id) return { ...entity, ...item };

          if (type === 'CHAT') {
            return {
              ...entity,
              chats: entity.chats.map((chat) =>
                chat.id === id ? { ...chat, ...item } : chat,
              ),
            };
          }

          return entity;
        }),
      };
    });
  };
  return { addChatbot, deleteChatbot, updateChatbot };
};

export const useWorkspaceChats = (mode: ChatbotMode) => {
  const { data } = useWorkspace();

  const chats = useMemo(() => {
    if (!data) return [];

    if (mode.type === 'chatbot') {
      if (mode.chatbotType === 'GROUP')
        return data.entities.filter(
          (val) =>
            val.type === 'CHAT' && val.parentChatbotId === mode.chatbotId,
        );

      return (
        data.entities
          .find((item) => item.id === mode.chatbotId)
          ?.chats.filter((val) => val.type === 'CHAT') ?? []
      );
    }

    const chatId = mode.chatId;

    const singleChat = data.entities.find((item) => item.id === chatId);

    if (!singleChat) return [];

    return [singleChat];
  }, [data, mode]);

  return chats;
};

export const formatChatAsItem = (
  chat: Chat,
  userId?: string,
  parentChatbotId?: string,
) => {
  return {
    ...chat,
    authors: null,
    summary: null,
    type: 'CHAT' as const,
    groupId: parentChatbotId ?? null,

    documentUrl: null,
    url: null,

    isIndexed: false,
    ingestionStatus: null,
    parentChatbotId,
    isTeam: false,
    teamId: null,
    userId: userId ?? '',
    groupCollapsed: false,
    displayOrder: null,
    chats: [],
    createdAt: new Date(),
    updatedAt: new Date(),
    includedSources: chat.includedSources as LinkedSource[],
  };
};

export const useAddChat = () => {
  const utils = api.useUtils();
  const { user } = useAuth();

  const addChat = (chat: Chat) => {
    utils.workspace.all.setData(undefined, (old) => {
      if (!old) return old;

      const sources = chat.includedSources as LinkedSource[];

      const oldEntities = [...old.entities];

      const mainSource = sources.find((item) => item.type === 'main');
      const mainEntity = oldEntities.find(
        (item) => item.id === mainSource?.chatbotId,
      );

      if (!mainEntity) {
        //this is a library level chat
        return {
          ...old,
          entities: [...oldEntities, formatChatAsItem(chat, user?.id)],
        };
      }

      if (mainEntity && mainEntity.type === 'GROUP')
        return {
          ...old,
          entities: [
            ...oldEntities,
            formatChatAsItem(chat, user?.id, mainEntity.id),
          ],
        };

      return {
        ...old,
        entities: oldEntities.map((item) =>
          item.id === mainEntity.id
            ? {
                ...item,
                chats: [
                  ...item.chats,
                  formatChatAsItem(chat, user?.id, mainEntity.id),
                ],
              }
            : item,
        ),
      };
    });
  };

  return { addChat };
};

export const useWorkspaceChatbot = (chatbotId: string) => {
  const { data } = useWorkspace();

  return { chatbot: data?.entities.find((item) => item.id === chatbotId) };
};

export const useChatStatus = (mode: ChatbotMode) => {
  const { data } = useWorkspace();
  const { updateChatbot } = useWorkspaceActions();
  const { shareData } = useIsSharedChat();

  const chatbot = useMemo(() => {
    if (mode.type !== 'chatbot') return null;

    const chatbotId = mode.chatbotId;
    return data?.entities.find((item) => item.id === chatbotId);
  }, [data, mode]);

  const shouldPoll = Boolean(
    (!chatbot?.isIndexed ||
      chatbot?.ingestionStatus === 'IN_PROGRESS' ||
      chatbot?.ingestionStatus === 'SUCCESS_PENDING_IMAGES') &&
      chatbot?.id,
  );
  const chatbotId = shouldPoll && chatbot ? [chatbot.id] : [];

  const { data: chatbotStatuses } = api.chatbot.ingestionStatuses.useQuery(
    chatbotId,
    {
      enabled: shouldPoll,
      refetchInterval: (query) => {
        const statuses = query.state?.data;
        if (!statuses) {
          return 2000;
        }

        const hasProcessing = statuses.some(
          (chatbot) =>
            chatbot.ingestionStatus?.status === 'SUCCESS_PENDING_IMAGES' ||
            chatbot.ingestionStatus?.status === 'IN_PROGRESS',
        );

        if (!hasProcessing) {
          return undefined;
        }

        const attemptCount = query.state.dataUpdateCount;

        if (attemptCount > 50) return undefined;
        if (attemptCount > 20) return 20000;
        if (attemptCount > 10) return 10000;
        if (attemptCount > 5) return 6000;
        return 2000;
      },
    },
  );

  const isLoading = useMemo(() => {
    if (mode.type === 'library' || mode.type === 'generalKnowledge')
      return false;

    if (shareData) return false;

    if (!chatbot) return true;

    if (chatbot.type === 'GROUP') return false;

    const isLoading =
      !chatbot.isIndexed &&
      mode.chatbotType !== 'RECORDING' &&
      mode.chatbotType !== 'NOTHING';

    return isLoading;
  }, [mode, chatbot, shareData]);

  useEffect(() => {
    if (!chatbotStatuses?.[0]) return;

    const updatedChatbot = chatbotStatuses[0];
    updateChatbot(updatedChatbot.id, {
      isIndexed: updatedChatbot.isIndexed,
      ingestionStatus: updatedChatbot.ingestionStatus?.status ?? null,
      documentUrl: updatedChatbot.documentUrl,
      name: updatedChatbot.name,
    });
  }, [chatbotStatuses]);

  return { isLoading };
};

export const useTeamId = () => {
  const { data } = useWorkspace();

  return data?.teamId;
};
